import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { challengesAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Loader2, ArrowLeft, Award, Download, Eye, Share2,
  Calendar, DollarSign, Trophy, FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Certificate Card Component
const CertificateCard = ({ certificate, onDownload, onView, downloading }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="group"
    >
      <Card className="bg-[hsl(220,20%,7%)] border-white/10 hover:border-cyan-500/30 transition-all overflow-hidden">
        {/* Certificate Preview Header */}
        <div className="relative h-48 bg-gradient-to-br from-blue-600/20 via-cyan-500/20 to-blue-600/20 border-b border-white/10">
          {/* Decorative Elements */}
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-4 left-4 w-20 h-20 border-2 border-yellow-400/50 rounded-full" />
            <div className="absolute top-6 left-6 w-16 h-16 border-2 border-yellow-400/30 rounded-full" />
            <div className="absolute bottom-4 right-4 w-16 h-16 border-2 border-cyan-400/50 rounded-full" />
          </div>
          
          {/* Certificate Content Preview */}
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4">
            <Award className="w-12 h-12 text-yellow-400 mb-2" />
            <h3 className="font-heading text-lg font-bold text-white">CERTIFICATE OF ACHIEVEMENT</h3>
            <p className="text-cyan-400 text-sm mt-1">Funded Trader</p>
            <p className="text-gray-400 text-xs mt-2">{certificate.package_name}</p>
          </div>

          {/* Status Badge */}
          <Badge className="absolute top-3 right-3 bg-green-500/20 text-green-400 border-green-500/30">
            FUNDED
          </Badge>
        </div>

        {/* Certificate Details */}
        <CardContent className="p-5">
          <div className="space-y-3 mb-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-400 text-sm flex items-center gap-2">
                <Trophy className="w-4 h-4" />
                Account
              </span>
              <span className="font-mono text-white">{certificate.package_name}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400 text-sm flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Funded Amount
              </span>
              <span className="font-mono text-cyan-400 font-medium">{formatCurrency(certificate.starting_balance)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400 text-sm flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Achieved On
              </span>
              <span className="text-white">{formatDate(certificate.funded_date || certificate.start_date)}</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button 
              onClick={() => onDownload(certificate.id, 'pdf')}
              disabled={downloading === certificate.id}
              className="flex-1 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
              data-testid={`download-cert-${certificate.id}`}
            >
              {downloading === certificate.id ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Download PDF
            </Button>
            <Button 
              variant="outline"
              onClick={() => onView(certificate.id)}
              className="border-white/10 text-white hover:bg-white/5"
              data-testid={`view-cert-${certificate.id}`}
            >
              <Eye className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const Certificates = () => {
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchCertificates();
  }, []);

  const fetchCertificates = async () => {
    try {
      const res = await challengesAPI.getMyChallenges();
      // Filter only funded accounts (they have certificates)
      const fundedAccounts = res.data.filter(c => c.status === 'funded');
      setCertificates(fundedAccounts);
    } catch (error) {
      toast.error('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };

  const downloadCertificate = async (challengeId, format = 'pdf') => {
    setDownloading(challengeId);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/certificates/${challengeId}/download?format=${format}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to download certificate');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `TRADERSCAPITAL_Certificate_${challengeId}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Certificate downloaded successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to download certificate');
    } finally {
      setDownloading(null);
    }
  };

  const viewCertificate = async (challengeId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/certificates/${challengeId}/download?format=pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Failed to load certificate');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Failed to view certificate');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(220,20%,4%)]">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(220,20%,4%)] pt-20" data-testid="certificates-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 mb-8"
        >
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/dashboard')}
            className="border-white/10 text-white hover:bg-white/5"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Dashboard
          </Button>
          <div>
            <h1 className="font-heading text-3xl font-bold text-white flex items-center gap-3">
              <Award className="w-8 h-8 text-yellow-400" />
              My Certificates
            </h1>
            <p className="text-gray-400 text-sm">Download and share your funded trader certificates</p>
          </div>
        </motion.div>

        {/* Certificates Grid */}
        {certificates.length > 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {certificates.map((cert, index) => (
              <motion.div
                key={cert.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + index * 0.05 }}
              >
                <CertificateCard 
                  certificate={cert}
                  onDownload={downloadCertificate}
                  onView={viewCertificate}
                  downloading={downloading}
                />
              </motion.div>
            ))}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="bg-[hsl(220,20%,7%)] border-white/10">
              <CardContent className="p-12 text-center">
                <div className="w-20 h-20 rounded-full bg-gradient-to-r from-blue-500/20 to-cyan-500/20 flex items-center justify-center mx-auto mb-6">
                  <Award className="w-10 h-10 text-gray-500" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-white mb-3">No Certificates Yet</h3>
                <p className="text-gray-400 mb-6 max-w-md mx-auto">
                  Complete a challenge and get funded to receive your official TRADERSCAPITAL certificate. 
                  Showcase your achievement to the world!
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    onClick={() => navigate('/challenges')}
                    className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 font-heading uppercase"
                  >
                    <Trophy className="w-4 h-4 mr-2" />
                    Start a Challenge
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => navigate('/dashboard')}
                    className="border-white/10 text-white hover:bg-white/5"
                  >
                    View My Accounts
                  </Button>
                </div>

                {/* Info Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-10">
                  <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                    <FileText className="w-6 h-6 text-blue-400 mx-auto mb-2" />
                    <p className="text-white font-medium">PDF Format</p>
                    <p className="text-gray-500 text-xs">High-quality downloadable</p>
                  </div>
                  <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                    <Share2 className="w-6 h-6 text-cyan-400 mx-auto mb-2" />
                    <p className="text-white font-medium">Shareable</p>
                    <p className="text-gray-500 text-xs">Share on social media</p>
                  </div>
                  <div className="p-4 rounded-lg bg-white/5 border border-white/10">
                    <Award className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
                    <p className="text-white font-medium">Official</p>
                    <p className="text-gray-500 text-xs">Verified achievement</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Certificates;
